import React, {useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Heading,
    Text,
    Image,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {usePromotions} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getSelectedBonusQuantityForBonusDiscountLineItem} from '../../../utils/basket-utils'
import {useSiteContext} from '../../../contexts/site'
import {setBonusModalTargetPromotionId} from '../../../utils/bonus-modal-target'
import {GWP_PROMOTION_ID_REGEX, PWP_PROMOTION_ID_REGEX} from '../../../constants'
import {getUrl} from '../../../utils/cloudinary-utils'
import useTagManager from '../../../hooks/use-tag-manager'

/**
 * Cart-level callout message for active GWP/PWP promotions.
 * - Uses promo custom fields so Web Merch can update copy without code changes.
 * - If both GWP and PWP are active, GWP callout(s) take priority.
 * - Includes stable selectors for Monetate.
 */
const GwpPwpCartCallout = ({basket}) => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const {setForceOpenGwpDrawer, setOpenBonusModal} = useSiteContext()
    const tagManager = useTagManager()
    const calloutGroups = useMemo(() => {
        const bonusDiscountLineItems = basket?.bonusDiscountLineItems || []
        if (!bonusDiscountLineItems.length) return []

        const groupsByPromotionId = new Map()

        for (const bonusDiscountLineItem of bonusDiscountLineItems) {
            const promotionId = bonusDiscountLineItem?.promotionId
            if (!promotionId) continue

            const maxBonusItems = bonusDiscountLineItem?.maxBonusItems || 0
            const selectedBonusQty = getSelectedBonusQuantityForBonusDiscountLineItem(
                basket,
                bonusDiscountLineItem?.id
            )

            const group = groupsByPromotionId.get(promotionId) || {
                promotionId,
                bonusDiscountLineItems: [],
                maxBonusItems: 0,
                selectedBonusQty: 0
            }

            group.bonusDiscountLineItems.push(bonusDiscountLineItem)
            group.maxBonusItems += maxBonusItems
            group.selectedBonusQty += selectedBonusQty

            groupsByPromotionId.set(promotionId, group)
        }

        const groups = Array.from(groupsByPromotionId.values())
            .map((g) => ({
                ...g,
                remainingBonusQty: Math.max(0, (g.maxBonusItems || 0) - (g.selectedBonusQty || 0))
            }))
            .filter((g) => g.remainingBonusQty > 0)
            .sort((a, b) => {
                const aIsGwp = GWP_PROMOTION_ID_REGEX.test(String(a.promotionId))
                const bIsGwp = GWP_PROMOTION_ID_REGEX.test(String(b.promotionId))
                if (aIsGwp === bIsGwp) return 0
                return aIsGwp ? -1 : 1
            })

        return groups
    }, [basket])

    const promoIds = useMemo(
        () => calloutGroups.map((g) => g.promotionId).filter(Boolean),
        [calloutGroups]
    )
    const enabled = !isServer && promoIds.length > 0
    const {data: promosResponse, isLoading: isPromotionsLoading} = usePromotions(
        {
            parameters: {
                ids: promoIds
            }
        },
        {
            enabled
        }
    )

    const promoDetailsById = useMemo(() => {
        const list = promosResponse?.data || []
        return new Map(list.map((p) => [p?.id, p]))
    }, [promosResponse])

    useEffect(() => {
        if (isPromotionsLoading) {
            return
        }
        promosResponse?.data?.forEach((promo) => {
            if (
                (GWP_PROMOTION_ID_REGEX?.test(promo?.id) ||
                    PWP_PROMOTION_ID_REGEX?.test(promo?.id)) &&
                !promo?.c_renderPwpCarousel &&
                promo?.c_promotionHeader
            ) {
                window.__dataLayerLoaded = true
                tagManager.promoMessageImpression({
                    id: promo?.id,
                    name: promo?.c_promotionHeader,
                    position: 'cart',
                    creative:
                        (GWP_PROMOTION_ID_REGEX?.test(promo?.id) ? 'GWP' : 'PWP') +
                        ` - ${promo?.c_promotionHeader}`
                })
                window.__dataLayerLoaded = false
            }
        })
    }, [promosResponse?.data?.length])

    if (!calloutGroups.length || isPromotionsLoading) return null

    return (
        <Box id="cart-gwp-pwp-callouts" data-testid="cart-gwp-pwp-callouts">
            {calloutGroups.map((group) => {
                const promo = promoDetailsById.get(group.promotionId)
                if (promo?.c_renderPwpCarousel) return null

                const isGwp = GWP_PROMOTION_ID_REGEX.test(String(group.promotionId))
                const promoType = isGwp ? 'GWP' : 'PWP'

                const remaining = group.remainingBonusQty
                const defaultHeading = isGwp
                    ? 'Select Your Free Gift'
                    : `Select ${remaining} Bonus Product${remaining === 1 ? '' : 's'}`
                const heading = promo?.c_promotionHeader || promo?.name || defaultHeading
                const subHeading =
                    promo?.c_promotionSubHeader || "You've Qualified for this exclusive offer"

                return (
                    <Box
                        key={group.promotionId}
                        id={`cart-gwp-pwp-callout-${group.promotionId}`}
                        data-testid="cart-gwp-pwp-callout"
                        data-promo-type={promoType}
                        data-promo-id={group.promotionId}
                        cursor="pointer"
                        role="button"
                        tabIndex={0}
                        {...cartStyles.bonusProductsCallout}
                        onClick={() => {
                            setBonusModalTargetPromotionId(group.promotionId)
                            setOpenBonusModal(true)
                            setForceOpenGwpDrawer(true)
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setBonusModalTargetPromotionId(group.promotionId)
                                setOpenBonusModal(true)
                                setForceOpenGwpDrawer(true)
                            }
                        }}
                        mb="10px"
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="flex-start"
                            gap="15px"
                            className="callout-flex"
                        >
                            <Image
                                src={getUrl('UIImages/images/interface/tag-icon.svg')}
                                width="29px"
                                height="29px"
                                alt={promoType + ' Icon'}
                            />
                            <Box width="100%" className="callout-box">
                                <Heading as="h3" {...cartStyles.bonusProductsCalloutHeading}>
                                    {heading}
                                </Heading>
                                <Text {...cartStyles.bonusProductsCalloutText}>{subHeading}</Text>
                            </Box>
                            <Image
                                src={getUrl('UIImages/images/redesign/arrow-default.svg')}
                                width="15px"
                                height="15px"
                                alt="Arrow Icon"
                            />
                        </Flex>
                    </Box>
                )
            })}
        </Box>
    )
}

GwpPwpCartCallout.propTypes = {
    basket: PropTypes.object
}

export default GwpPwpCartCallout
